import { useRedNavigate } from '@/dataImplementation/useRedNavigate'
import { PATHS } from '@/router/paths'
import { getString } from '@vrw/data'
import React, { useMemo, useState, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { generateSearchQueryString } from '../../helpers/searchQueryParams'
import { CalendarPicker } from './CalendarPicker'
import { FlightSearchForm } from './FlightSearchForm'
import { QuestionForm } from './QuestionForm'
import { OnChangeType, SearchForm, SearchFormKey, useSearchForm } from './useSearchForm'
import { useRecentSearches } from './useRecentSearches'
import { SideSheet } from '../SideSheet/SideSheet'
import { Icon, VisuallyHidden } from '@red-ui/components'

export type Props = {
  mainSheetTitle?: string
  editMode?: boolean
  initData?: SearchForm | null
}

export const fields = ['departure', 'destination', 'date', 'passengers']

export const SearchSideSheet = ({ mainSheetTitle, editMode = false, initData }: Props) => {
  const { pathname, search } = useLocation()
  const [openSheet, setOpenSheet] = useState<SearchFormKey | null>(null)
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0)
  const { form, onChange: onChangeField, onReset } = useSearchForm(initData)
  const redNavigate = useRedNavigate()
  const { recentSearches, saveSearch } = useRecentSearches()

  const open = pathname.endsWith('/edit')

  const handleSubSheetClose = useCallback(() => {
    setOpenSheet(null)
  }, [])

  const handleMainClose = useCallback(() => {
    if (openSheet) handleSubSheetClose()
    setCurrentQuestionIdx(0)
    onReset()
    redNavigate(`${pathname.replace('/edit', '')}${search}`)
  }, [openSheet, handleSubSheetClose, onReset, redNavigate, pathname, search])

  const onChange: OnChangeType = useCallback(
    (field, value) => {
      if (!editMode && currentQuestionIdx < fields.length - 1) {
        setCurrentQuestionIdx(currentQuestionIdx + 1)
      }

      onChangeField(field, value)
    },
    [currentQuestionIdx, editMode, onChangeField]
  )

  const onChangeAndClose = useCallback(
    (field: SearchFormKey) => (value: SearchForm[SearchFormKey]) => {
      handleSubSheetClose()
      onChange(field, value)
    },
    [handleSubSheetClose, onChange]
  )

  const onSheetOpen = (type: SearchFormKey) => () => {
    setOpenSheet(type)
  }

  const onSubmitForm = useCallback(() => {
    const searchQueryStr = generateSearchQueryString(form)
    redNavigate(`${PATHS.REDSKY_SEARCH_RESULTS}?${searchQueryStr}`)
  }, [form, redNavigate])

  const { title, component } = useMemo(() => {
    switch (openSheet) {
      case 'departure':
      case 'destination':
        return {
          title: getString(`redSkySearch.question.${openSheet}.label`),
          component: (
            <FlightSearchForm
              onClose={handleSubSheetClose}
              title={getString(`redSkySearch.question.${openSheet}.label`)}
              info={getString(`redSkySearch.question.${openSheet}.input.info`)}
              onSelect={onChangeAndClose(openSheet)}
              value={form[openSheet]?.airportName}
              recentSearches={recentSearches}
              saveSearch={saveSearch}
            />
          ),
        }
      case 'date':
        return {
          title: getString('redSkySearch.question.date.title'),
          component: <CalendarPicker value={form.date} onChange={onChangeAndClose('date')} />,
        }
      default:
        return {
          title: mainSheetTitle ?? getString('redSkySearch.title'),
          component: (
            <QuestionForm
              onClose={handleMainClose}
              onSubSheetOpen={onSheetOpen}
              editMode={editMode}
              form={form}
              onChange={onChange}
              onSubmit={onSubmitForm}
              currentQuestionIdx={currentQuestionIdx}
              onChangeQuestionIdx={setCurrentQuestionIdx}
            />
          ),
        }
    }
  }, [
    currentQuestionIdx,
    editMode,
    form,
    handleMainClose,
    handleSubSheetClose,
    mainSheetTitle,
    onChange,
    onChangeAndClose,
    onSubmitForm,
    openSheet,
    recentSearches,
    saveSearch,
  ])

  return (
    <SideSheet.Root open={open} onClose={handleMainClose}>
      <SideSheet.Header
        {...(openSheet
          ? {
              closeButton: (
                <SideSheet.CloseButton icon={Icon.ChevronLeft} onPress={handleSubSheetClose}>
                  <VisuallyHidden>{getString('redSkySearch.sidesheet.button.goBack')}</VisuallyHidden>
                </SideSheet.CloseButton>
              ),
              buttonPosition: 'right',
            }
          : {})}>
        {title}
      </SideSheet.Header>
      {component}
    </SideSheet.Root>
  )
}
